import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  Tooltip,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Autocomplete
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Clear as ClearIcon,
  Search as SearchIcon,
  Upload as UploadIcon,
  Refresh as RefreshIcon,
  GetApp as ExportIcon,
  PictureAsPdf as PdfIcon
} from '@mui/icons-material';
import { useAuth } from '../../../../auth/useAuth';
import { useHedgeFundData } from '../../contexts/HedgeFundDataContext';
import { useTableSort } from '../../../../hooks/useTableSort';
import SortableTableHeader from '../../../../components/SortableTableHeader/SortableTableHeader';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Types
interface Performance {
  PerfID: number;
  BackstopID: number;
  ClassID: number;
  FundName: string;
  ClassName: string;
  Currency: string;
  PerfDate: string;
  PerfValue: number;
  PerfQualityName: string;
  PerfTypeName: string;
  Timestamp: string;
  UserName: string;
}

interface Fund {
  BackstopID: number;
  FundName: string;
  Invested: boolean;
}

interface FundClass {
  ClassID: number;
  BackstopID: number;
  ClassName: string;
  Currency: string;
  IsDefault: boolean;
}

interface PerfQuality {
  PerfQualityID: number;
  PerfQualityName: string;
}

interface PerfType {
  PerfTypeID: number;
  PerfTypeName: string;
}

const PerformanceManagement: React.FC = () => {
  const { getAccessToken } = useAuth();
  const {
    funds: allFunds,
    fundClasses,
    perfQualities,
    perfTypes,
    performances,
    monitorThresholds,
    loading: contextLoading,
    getInvestedFunds,
    addPerformance,
    updatePerformance,
    removePerformance,
    refreshPerformances
  } = useHedgeFundData();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Form state
  const [formData, setFormData] = useState({
    BackstopID: '',
    FundName: '',
    ClassID: '',
    PerfDate: new Date().toISOString().split('T')[0],
    PerfValue: '',
    PerfQualityID: '',
    PerfTypeID: '1'
  });
  const [editingId, setEditingId] = useState<number | null>(null);
  const [originalEditValues, setOriginalEditValues] = useState<typeof formData | null>(null);
  const [appliedFilters, setAppliedFilters] = useState({
    BackstopID: '',
    FundName: '',
    ClassID: '',
    PerfDate: '',
    PerfValue: '',
    PerfQualityID: '',
    PerfTypeID: ''
  });
  
  // Filter state
  const [investedOnly, setInvestedOnly] = useState(true);
  const [selectedFund, setSelectedFund] = useState<string>('');
  const [selectedClass, setSelectedClass] = useState<string>('');
  const [clearCount, setClearCount] = useState(0); // Force remount on clear
  
  // Table state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [performanceToDelete, setPerformanceToDelete] = useState<Performance | null>(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  
  // Import state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importResults, setImportResults] = useState<{
    success: boolean;
    message: string;
    details?: {
      imported_count: number;
      total_rows_processed: number;
      errors?: string[];
    };
  } | null>(null);

  // Monthly ROR upload dialog state
  const [rorDialogOpen, setRorDialogOpen] = useState(false);
  const [rorUploading, setRorUploading] = useState(false);
  const [pendingRorUpload, setPendingRorUpload] = useState<{
    backstopId: number;
    perfDate: string;
    perfValue: number;
    fundName: string;
  } | null>(null);

  // Get filtered funds based on invested only setting - with null safety
  const funds = investedOnly ? (getInvestedFunds() || []) : (allFunds || []);

  // Handle form input changes
  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle fund selection change
  const handleFundChange = (value: string | number) => {
    const backstopId = String(value);
    setSelectedFund(backstopId);
    setFormData(prev => ({ ...prev, BackstopID: backstopId, ClassID: '' }));
    setSelectedClass('');
    // Auto-apply search when fund changes
    setTimeout(() => handleSearch(), 0);
  };

  // Handle class selection change
  const handleClassChange = (classId: string) => {
    setSelectedClass(classId);
    setFormData(prev => ({ ...prev, ClassID: classId }));
    // Auto-apply search when class changes
    setTimeout(() => handleSearch(), 0);
  };

  // Handle invested only filter change
  const handleInvestedOnlyChange = (checked: boolean) => {
    setInvestedOnly(checked);
  };

  // Check if a date is the last working day (Mon-Fri) of its month
  const isLastCalendarDay = (dateStr: string): boolean => {
    const date = new Date(dateStr + 'T00:00:00');
    const year = date.getFullYear();
    const month = date.getMonth();
    const lastDay = new Date(year, month + 1, 0);
    return date.getDate() === lastDay.getDate();
  };

  // Check if a fund has an active Monthly ROR (%) threshold (FieldID = 4)
  const hasMonthlyRorThreshold = (backstopId: number): boolean => {
    return (monitorThresholds || []).some(
      t => t.BackstopID === backstopId && t.FieldID === 4 && t.Newest === true
    );
  };

  // After successful performance save, check if we should prompt for Monthly ROR upload
  const checkAndPromptRorUpload = (backstopId: number, perfDate: string, perfValue: number, fundName: string) => {
    if (isLastCalendarDay(perfDate) && hasMonthlyRorThreshold(backstopId)) {
      setPendingRorUpload({ backstopId, perfDate, perfValue, fundName });
      setRorDialogOpen(true);
    }
  };

  // Upload performance value to Monitor_Data as Monthly ROR (%)
  const handleRorUpload = async () => {
    if (!pendingRorUpload) return;

    setRorUploading(true);
    try {
      const token = await getAccessToken();
      if (!token) throw new Error('No access token available');

      const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';

      // Use upsert endpoint — backend handles insert vs update in one atomic operation
      const response = await fetch(`${baseUrl}/api/hedgefund/monitor-data/upsert`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          BackstopID: pendingRorUpload.backstopId,
          FieldID: 4,
          MonitorDate: pendingRorUpload.perfDate,
          FieldValue: pendingRorUpload.perfValue,
          Note: 'Updated via the performance table'
        })
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || 'Failed to upload Monthly ROR to monitoring data');
      }

      const result = await response.json();
      const action = result.action === 'updated' ? 'updated in' : 'uploaded to';
      setSuccess(prev => (prev || '') + ` Monthly ROR (%) ${action} Monitoring Data.`);
    } catch (err) {
      setError(`Performance saved, but Monthly ROR upload failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setRorUploading(false);
      setRorDialogOpen(false);
      setPendingRorUpload(null);
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    // Validate that Quality is not "All" (empty string)
    if (!formData.PerfQualityID) {
      setError('Please select a specific quality. "All" cannot be used when saving performance data.');
      return;
    }
    
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('No access token available');
      }
      
      // Use selectedClass which already contains the correct ClassID
      const classIdToSave = selectedClass;
      if (!classIdToSave) {
        throw new Error('No fund class selected');
      }
      
      const url = editingId 
        ? `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}/api/hedgefund/performance/${editingId}`
        : `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}/api/hedgefund/performance`;
      
      const method = editingId ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          BackstopID: parseInt(formData.BackstopID),
          ClassID: parseInt(classIdToSave), // Use the actual ClassID
          PerfDate: formData.PerfDate,
          PerfValue: parseFloat(formData.PerfValue) / 100, // Convert percentage to decimal
          PerfQualityID: parseInt(formData.PerfQualityID),
          PerfTypeID: parseInt(formData.PerfTypeID)
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `Failed to ${editingId ? 'update' : 'create'} performance`);
      }
      
      setSuccess(`Performance ${editingId ? 'updated' : 'created'} successfully`);

      // Check for Monthly ROR upload opportunity (before clearing form)
      // Use the raw display value (e.g. 2.45 for 2.45%) — NOT the decimal sent to performance API
      const displayValue = parseFloat(formData.PerfValue);
      const fundName = (allFunds || []).find(f => f?.BackstopID?.toString() === formData.BackstopID)?.FundName || '';
      checkAndPromptRorUpload(parseInt(formData.BackstopID), formData.PerfDate, displayValue, fundName);

      // After saving (both add and update), retain fund/class/quality/type but clear date and value, then apply search
      setFormData(prev => ({
        ...prev,
        PerfDate: '',
        PerfValue: '',
      }));
      setEditingId(null);
      setOriginalEditValues(null);

      // Trigger search with retained values
      setTimeout(() => handleSearch(), 100);

      refreshPerformances();

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  // Handle edit - with comprehensive null safety
  const handleEdit = (performance: Performance) => {
    if (!performance) return;
    
    // Find the actual ClassID from the performance record
    const fundClass = (fundClasses || []).find(cls => 
      cls && cls.BackstopID === performance.BackstopID && 
      cls.ClassName === performance.ClassName
    );
    
    // Find quality and type IDs by matching names - with safe conversion
    const qualityId = (perfQualities || []).find(q => q && q.PerfQualityName === performance.PerfQualityName)?.PerfQualityID;
    const typeId = (perfTypes || []).find(t => t && t.PerfTypeName === performance.PerfTypeName)?.PerfTypeID;
    
    setFormData({
      BackstopID: performance.BackstopID != null ? String(performance.BackstopID) : '',
      FundName: performance.FundName || '',
      ClassID: performance.ClassName || '', // Store class NAME, not ID
      PerfDate: performance.PerfDate || '',
      PerfValue: performance.PerfValue != null ? String(performance.PerfValue * 100) : '', // Convert decimal to percentage
      PerfQualityID: qualityId != null ? String(qualityId) : '1',
      PerfTypeID: typeId != null ? String(typeId) : '1'
    });
    setSelectedFund(performance.BackstopID != null ? String(performance.BackstopID) : '');
    setSelectedClass(fundClass?.ClassID != null ? String(fundClass.ClassID) : '');
    setEditingId(performance.PerfID || null);
    
    // Store original values to detect changes
    setOriginalEditValues({
      BackstopID: performance.BackstopID != null ? String(performance.BackstopID) : '',
      FundName: performance.FundName || '',
      ClassID: performance.ClassName || '', // Store class NAME, not ID
      PerfDate: performance.PerfDate || '',
      PerfValue: performance.PerfValue != null ? String(performance.PerfValue * 100) : '',
      PerfQualityID: qualityId != null ? String(qualityId) : '1',
      PerfTypeID: typeId != null ? String(typeId) : '1'
    });
  };

  // Handle delete
  const handleDelete = async () => {
    if (!performanceToDelete) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('No access token available');
      }
      
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}/api/hedgefund/performance/${performanceToDelete.PerfID}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete performance');
      }
      
      setSuccess('Performance deleted successfully');
      setDeleteDialogOpen(false);
      setPerformanceToDelete(null);
      refreshPerformances();
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setLoading(false);
    }
  };

  // Handle search button click - includes all filters
  const handleSearch = (includeAdvancedFilters: boolean = true) => {
    // Convert ClassID to ClassName for filtering if it's a number
    let classFilterValue = formData.ClassID;
    if (formData.ClassID && !isNaN(parseInt(formData.ClassID))) {
      const fundClass = fundClasses.find(cls => cls.ClassID.toString() === formData.ClassID);
      if (fundClass) {
        classFilterValue = fundClass.ClassName;
      }
    }
    
    // When auto-filtering (fund/class change), only apply fund and class filters
    // When manually searching (Search button), apply all filters including date, value, quality
    setAppliedFilters({ 
      BackstopID: formData.BackstopID,
      FundName: formData.FundName,
      ClassID: classFilterValue,
      PerfDate: includeAdvancedFilters ? formData.PerfDate : '', // Only when Search clicked
      PerfValue: includeAdvancedFilters ? formData.PerfValue : '', // Only when Search clicked
      PerfQualityID: includeAdvancedFilters ? formData.PerfQualityID : '', // Only when Search clicked
      PerfTypeID: includeAdvancedFilters ? formData.PerfTypeID : ''
    });
    setPage(0); // Reset pagination when filtering
  };

  // Handle clear form
  const handleClear = () => {
    // Find "MTD" type ID (default to 1 if not found)
    const mtdType = perfTypes.find(t => t.PerfTypeName === 'MTD');
    
    setFormData({
      BackstopID: '',
      FundName: '',
      ClassID: '',
      PerfDate: '',
      PerfValue: '',
      PerfQualityID: '',
      PerfTypeID: mtdType ? String(mtdType.PerfTypeID) : '1'
    });
    setAppliedFilters({
      BackstopID: '',
      FundName: '',
      ClassID: '',
      PerfDate: '',
      PerfValue: '',
      PerfQualityID: '',
      PerfTypeID: ''
    });
    setEditingId(null);
    setOriginalEditValues(null);
    setSelectedFund('');
    setSelectedClass('');
    // Increment clearCount to force Autocomplete remount
    setClearCount(prev => prev + 1);
  };

  // Check if any filter criteria are set in form
  const hasFilterCriteria = formData.BackstopID || formData.FundName || formData.ClassID || 
    (formData.PerfDate !== new Date().toISOString().split('T')[0]) || formData.PerfValue ||
    formData.PerfQualityID || (formData.PerfTypeID !== '1');

  // Check if any filters are currently applied
  const hasAppliedFilters = appliedFilters.BackstopID || appliedFilters.FundName || appliedFilters.ClassID || 
    appliedFilters.PerfDate || appliedFilters.PerfValue ||
    appliedFilters.PerfQualityID || appliedFilters.PerfTypeID;

  // Filter performances - with comprehensive null safety
  const filteredPerformances = (performances || []).filter(performance => {
    if (!performance) return false;
    
    // Apply invested funds filter using complete fund list
    const matchesInvestedFilter = !investedOnly || (allFunds || []).find(fund => fund && fund.BackstopID === performance.BackstopID)?.Invested;
    
    // Apply form-based filters only if they have been applied via search button
    const matchesFormFilters = !hasAppliedFilters || (
      (!appliedFilters.BackstopID || 
        (performance.BackstopID != null && String(performance.BackstopID) === appliedFilters.BackstopID) ||
        (performance.BackstopID != null && String(performance.BackstopID).startsWith(appliedFilters.BackstopID))
      ) &&
      (!appliedFilters.FundName || 
        (performance.FundName && performance.FundName.toLowerCase().includes(appliedFilters.FundName.toLowerCase()))
      ) &&
      (!appliedFilters.ClassID || (performance.ClassName && performance.ClassName.toLowerCase().includes(appliedFilters.ClassID.toLowerCase()))) &&
      (!appliedFilters.PerfDate || performance.PerfDate === appliedFilters.PerfDate) &&
      (!appliedFilters.PerfValue || (performance.PerfValue != null && Math.abs(performance.PerfValue * 100 - parseFloat(appliedFilters.PerfValue)) < 0.01)) &&
      (!appliedFilters.PerfQualityID || (performance.PerfQualityName && performance.PerfQualityName === (perfQualities || []).find(q => q && q.PerfQualityID != null && String(q.PerfQualityID) === appliedFilters.PerfQualityID)?.PerfQualityName)) &&
      (!appliedFilters.PerfTypeID || (performance.PerfTypeName && performance.PerfTypeName === (perfTypes || []).find(t => t && t.PerfTypeID != null && String(t.PerfTypeID) === appliedFilters.PerfTypeID)?.PerfTypeName))
    );
    
    return matchesInvestedFilter && matchesFormFilters;
  });

  // Apply sorting to filtered data
  const { sortedData, handleSort, getSortDirection, getSortIndex } = useTableSort({
    data: filteredPerformances,
    initialSort: [{ field: 'PerfDate', direction: 'desc' }] // Default sort by date descending
  });

  // Paginated performances
  const paginatedPerformances = sortedData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Handle file selection for import
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setImportResults(null); // Clear previous results
    }
  };

  // Handle import track record
  const handleImportTrack = async () => {
    if (!selectedFile || !selectedFund) return;
    
    setLoading(true);
    setError(null);
    setImportResults(null);
    
    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('No access token available');
      }
      
      // Get the selected fund class ID
      let classIdToUse = selectedClass;
      if (!classIdToUse) {
        // Use the default class for the fund if no class is selected
        const defaultClass = (fundClasses || []).find(cls => 
          cls && cls.BackstopID != null && String(cls.BackstopID) === selectedFund && cls.IsDefault
        );
        if (defaultClass) {
          classIdToUse = String(defaultClass.ClassID);
        } else {
          // Use the first available class for the fund
          const firstClass = (fundClasses || []).find(cls => 
            cls && cls.BackstopID != null && String(cls.BackstopID) === selectedFund
          );
          if (firstClass) {
            classIdToUse = String(firstClass.ClassID);
          } else {
            throw new Error('No fund class found for the selected fund');
          }
        }
      }
      
      // Create FormData for file upload
      const uploadFormData = new FormData();
      uploadFormData.append('file', selectedFile);
      uploadFormData.append('backstop_id', selectedFund);
      uploadFormData.append('class_id', classIdToUse);
      uploadFormData.append('perf_quality_id', formData.PerfQualityID || '1');
      uploadFormData.append('perf_type_id', formData.PerfTypeID || '1');
      
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}/api/hedgefund/import/performance-track`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: uploadFormData
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.detail || 'Failed to import performance track');
      }
      
      setImportResults({
        success: true,
        message: result.message,
        details: {
          imported_count: result.imported_count,
          total_rows_processed: result.total_rows_processed,
          errors: result.errors
        }
      });
      
      // Refresh performance data
      refreshPerformances();
      
    } catch (err) {
      setImportResults({
        success: false,
        message: err instanceof Error ? err.message : 'Import failed'
      });
    } finally {
      setLoading(false);
    }
  };

  // Check if form values have changed from original (when editing)
  const hasChangesFromOriginal = editingId && originalEditValues && (
    formData.BackstopID !== originalEditValues.BackstopID ||
    formData.ClassID !== originalEditValues.ClassID ||
    formData.PerfDate !== originalEditValues.PerfDate ||
    formData.PerfValue !== originalEditValues.PerfValue ||
    formData.PerfQualityID !== originalEditValues.PerfQualityID ||
    formData.PerfTypeID !== originalEditValues.PerfTypeID
  );

  // Handle save as new (when editing but user wants to add as new)
  const handleSaveAsNew = async () => {
    // Validate that Quality is not "All" (empty string)
    if (!formData.PerfQualityID) {
      setError('Please select a specific quality. "All" cannot be used when saving performance data.');
      return;
    }
    
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('No access token available');
      }
      
      // Use selectedClass which already contains the correct ClassID
      const classIdToSave = selectedClass;
      if (!classIdToSave) {
        throw new Error('No fund class selected');
      }
      
      // Force POST request by using the add endpoint
      const url = `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}/api/hedgefund/performance`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          BackstopID: parseInt(formData.BackstopID),
          ClassID: parseInt(classIdToSave),
          PerfDate: formData.PerfDate,
          PerfValue: parseFloat(formData.PerfValue) / 100,
          PerfQualityID: parseInt(formData.PerfQualityID),
          PerfTypeID: parseInt(formData.PerfTypeID)
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create new performance');
      }
      
      setSuccess('New performance created successfully');

      // Check for Monthly ROR upload opportunity (before clearing form)
      // Use the raw display value (e.g. 2.45 for 2.45%) — NOT the decimal sent to performance API
      const displayValue = parseFloat(formData.PerfValue);
      const fundName = (allFunds || []).find(f => f?.BackstopID?.toString() === formData.BackstopID)?.FundName || '';
      checkAndPromptRorUpload(parseInt(formData.BackstopID), formData.PerfDate, displayValue, fundName);

      // Clear form for next entry
      setFormData(prev => ({
        ...prev,
        PerfDate: '',
        PerfValue: '',
      }));
      setEditingId(null);
      setOriginalEditValues(null);

      // Trigger search with retained values
      setTimeout(() => handleSearch(), 100);

      refreshPerformances();

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  // Handle CSV export
  const handleExportCSV = () => {
    try {
      if (filteredPerformances.length === 0) {
        setError('No data to export');
        return;
      }

      let csvContent = 'Performance Management Export\n';
      csvContent += `Export Date: ${new Date().toLocaleString()}\n`;
      csvContent += `Total Records: ${filteredPerformances.length}\n\n`;
      
      csvContent += 'Applied Filters:\n';
      if (appliedFilters.BackstopID) csvContent += `BackstopID: ${appliedFilters.BackstopID}\n`;
      if (appliedFilters.FundName) csvContent += `Fund Name: ${appliedFilters.FundName}\n`;
      if (appliedFilters.ClassID) csvContent += `Class: ${appliedFilters.ClassID}\n`;
      if (appliedFilters.PerfDate) csvContent += `Date: ${appliedFilters.PerfDate}\n`;
      if (appliedFilters.PerfValue) csvContent += `Value: ${appliedFilters.PerfValue}%\n`;
      if (appliedFilters.PerfQualityID) {
        const quality = perfQualities.find(q => q.PerfQualityID.toString() === appliedFilters.PerfQualityID);
        csvContent += `Quality: ${quality?.PerfQualityName || appliedFilters.PerfQualityID}\n`;
      }
      if (appliedFilters.PerfTypeID) {
        const type = perfTypes.find(t => t.PerfTypeID.toString() === appliedFilters.PerfTypeID);
        csvContent += `Type: ${type?.PerfTypeName || appliedFilters.PerfTypeID}\n`;
      }
      csvContent += `Invested Only: ${investedOnly ? 'Yes' : 'No'}\n`;
      if (!hasAppliedFilters && !investedOnly) csvContent += 'No filters applied (showing all data)\n';
      
      csvContent += '\n';
      csvContent += 'BackstopID,Fund Name,Class,Date,Value (%),Quality,Type,Timestamp,User\n';
      
      sortedData.forEach(row => {
        const date = new Date(row.PerfDate).toLocaleDateString();
        const value = (row.PerfValue * 100).toFixed(2);
        const timestamp = new Date(row.Timestamp).toLocaleString();
        
        csvContent += `${row.BackstopID},"${row.FundName}","${row.ClassName}","${date}",${value},"${row.PerfQualityName}","${row.PerfTypeName}","${timestamp}","${row.UserName}"\n`;
      });

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `performance_data_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setSuccess('Data exported to CSV successfully');
    } catch (err) {
      setError(`CSV export failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  // Handle PDF export
  const handleExportPDF = () => {
    try {
      if (filteredPerformances.length === 0) {
        setError('No data to export');
        return;
      }

      const doc = new jsPDF('landscape', 'pt', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      
      // Title
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('Performance Management Export', pageWidth / 2, 30, { align: 'center' });
      
      // Export info
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text(`Export Date: ${new Date().toLocaleString()}`, 40, 50);
      doc.text(`Total Records: ${filteredPerformances.length}`, 40, 62);
      
      // Filter settings
      let yPos = 74;
      doc.setFont('helvetica', 'bold');
      doc.text('Applied Filters:', 40, yPos);
      doc.setFont('helvetica', 'normal');
      yPos += 12;
      
      if (appliedFilters.BackstopID) {
        doc.text(`• BackstopID: ${appliedFilters.BackstopID}`, 50, yPos);
        yPos += 12;
      }
      if (appliedFilters.FundName) {
        doc.text(`• Fund Name: ${appliedFilters.FundName}`, 50, yPos);
        yPos += 12;
      }
      if (appliedFilters.ClassID) {
        doc.text(`• Class: ${appliedFilters.ClassID}`, 50, yPos);
        yPos += 12;
      }
      if (appliedFilters.PerfDate) {
        doc.text(`• Date: ${appliedFilters.PerfDate}`, 50, yPos);
        yPos += 12;
      }
      if (appliedFilters.PerfValue) {
        doc.text(`• Value: ${appliedFilters.PerfValue}%`, 50, yPos);
        yPos += 12;
      }
      if (appliedFilters.PerfQualityID) {
        const quality = perfQualities.find(q => q.PerfQualityID.toString() === appliedFilters.PerfQualityID);
        doc.text(`• Quality: ${quality?.PerfQualityName || appliedFilters.PerfQualityID}`, 50, yPos);
        yPos += 12;
      }
      if (appliedFilters.PerfTypeID) {
        const type = perfTypes.find(t => t.PerfTypeID.toString() === appliedFilters.PerfTypeID);
        doc.text(`• Type: ${type?.PerfTypeName || appliedFilters.PerfTypeID}`, 50, yPos);
        yPos += 12;
      }
      doc.text(`• Invested Only: ${investedOnly ? 'Yes' : 'No'}`, 50, yPos);
      yPos += 12;
      
      if (!hasAppliedFilters && !investedOnly) {
        doc.text('• No filters applied (showing all data)', 50, yPos);
        yPos += 12;
      }
      
      yPos += 10;
      
      // Prepare table data
      const tableData = sortedData.map(row => [
        row.BackstopID.toString(),
        row.FundName,
        row.ClassName,
        new Date(row.PerfDate).toLocaleDateString(),
        `${(row.PerfValue * 100).toFixed(2)}%`,
        row.PerfQualityName,
        row.PerfTypeName,
        new Date(row.Timestamp).toLocaleDateString(),
        row.UserName
      ]);
      
      // Create table
      autoTable(doc, {
        head: [['BackstopID', 'Fund', 'Class', 'Date', 'Value', 'Quality', 'Type', 'Timestamp', 'User']],
        body: tableData,
        startY: yPos,
        styles: {
          fontSize: 7,
          cellPadding: 2,
          overflow: 'linebreak',
        },
        headStyles: {
          fillColor: [26, 35, 126],
          textColor: 255,
          fontStyle: 'bold',
          fontSize: 7,
        },
        columnStyles: {
          0: { cellWidth: 60 },
          1: { cellWidth: 100 },
          2: { cellWidth: 80 },
          3: { cellWidth: 70 },
          4: { cellWidth: 60, halign: 'right', fontStyle: 'bold' },
          5: { cellWidth: 70 },
          6: { cellWidth: 50 },
          7: { cellWidth: 70 },
          8: { cellWidth: 80 },
        },
        didDrawPage: (data) => {
          doc.setFontSize(8);
          doc.setFont('helvetica', 'normal');
          const pageNumber = doc.getCurrentPageInfo().pageNumber;
          doc.text(`Page ${pageNumber}`, pageWidth / 2, pageHeight - 20, { align: 'center' });
        },
        margin: { top: yPos, left: 40, right: 40, bottom: 30 },
      });
      
      // Save PDF
      doc.save(`performance_data_${new Date().toISOString().split('T')[0]}.pdf`);
      
      setSuccess('Data exported to PDF successfully');
    } catch (err) {
      setError(`PDF export failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  // Get currency for selected class
  const selectedClassCurrency = fundClasses.find(cls => cls.ClassID.toString() === selectedClass)?.Currency || '';

  // Add keyboard shortcuts for search (Ctrl+S) and clear (Ctrl+L)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check for Ctrl+S (or Cmd+S on Mac) for search
      if ((event.ctrlKey || event.metaKey) && event.key === 's') {
        event.preventDefault(); // Prevent browser save dialog
        handleSearch();
      }
      // Check for Ctrl+L (or Cmd+L on Mac) to clear form
      else if ((event.ctrlKey || event.metaKey) && event.key === 'l') {
        event.preventDefault(); // Prevent browser default
        handleClear();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []); // Empty dependency array to avoid recreating listener
  
  // Auto-filter when fund, class, quality, or type changes (after state has updated)
  useEffect(() => {
    // Auto-filter if we have a fund selected or quality/type changed, and we're not editing
    if ((formData.BackstopID || formData.PerfQualityID || formData.PerfTypeID !== '1') && !editingId) {
      handleSearch(false);
    }
  }, [formData.BackstopID, formData.ClassID, formData.PerfQualityID, formData.PerfTypeID]);

  return (
    <Box>
      <Typography variant="h5" component="h2" sx={{ mb: 3, fontWeight: 'bold', color: '#1a237e' }}>
        Performance Management
      </Typography>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Form Section */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }} data-form-section>
            <Typography variant="h6" sx={{ mb: 1.5 }}>
              {editingId ? 'Edit Performance' : hasFilterCriteria ? 'Add Performance / Search Filters' : 'Add Performance'}
            </Typography>
            {hasFilterCriteria && !editingId && (
              <Alert severity="info" sx={{ mb: 1.5, py: 0.5 }}>
                <Typography variant="body2">
                  Form fields are being used to filter the performance list. Click "Search" to apply filters or "Clear" to reset.
                </Typography>
              </Alert>
            )}

            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  label="BackstopID"
                  value={formData.BackstopID}
                  onChange={(e) => handleInputChange('BackstopID', e.target.value)}
                  fullWidth
                  type="number"
                  placeholder="Enter BackstopID"
                  size="small"
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                  size="small"
                options={funds}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  return option.FundName || '';
                }}
                value={funds.find(f => f.BackstopID?.toString() === formData.BackstopID) || null}
                onChange={(event, newValue) => {
                  if (newValue && typeof newValue !== 'string') {
                    // Find "Estimate" quality ID
                    const estimateQuality = perfQualities.find(q => q.PerfQualityName === 'Estimate');
                    
                    setFormData(prev => ({
                      ...prev,
                      BackstopID: newValue.BackstopID?.toString() || '',
                      FundName: newValue.FundName || '',
                      ClassID: '', // Reset class when fund changes
                      PerfQualityID: estimateQuality ? String(estimateQuality.PerfQualityID) : '' // Default to Estimate
                    }));
                    setSelectedFund(newValue.BackstopID?.toString() || '');
                    setSelectedClass('');
                  } else {
                    setFormData(prev => ({
                      ...prev,
                      BackstopID: '',
                      FundName: '',
                      ClassID: '',
                      PerfQualityID: '' // Reset quality when fund is cleared
                    }));
                    setSelectedFund('');
                    setSelectedClass('');
                  }
                }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Fund Name *"
                      placeholder="Select fund"
                      size="small"
                    />
                  )}
                renderOption={(props, option) => (
                  <li {...props}>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="body2">{option.FundName}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        BackstopID: {option.BackstopID}
                      </Typography>
                    </Box>
                  </li>
                )}
                isOptionEqualToValue={(option, value) => option.BackstopID === value.BackstopID}
                selectOnFocus
                clearOnBlur
                handleHomeEndKeys
              />

              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <Autocomplete
                  key={`class-${formData.BackstopID}-${clearCount}`}
                  size="small"
                  options={
                  formData.BackstopID 
                    ? Array.from(new Set(
                        (fundClasses || [])
                          .filter(cls => cls && cls.BackstopID != null && cls.BackstopID.toString() === formData.BackstopID)
                          .map(cls => cls.ClassName)
                      )).sort()
                    : Array.from(new Set((fundClasses || []).map(cls => cls?.ClassName).filter(Boolean))).sort()
                }
                value={
                  // Return null for empty, otherwise find class name or return the ClassID value
                  formData.ClassID === '' ? null : (
                    !isNaN(parseInt(formData.ClassID)) 
                      ? fundClasses.find(cls => cls.ClassID.toString() === formData.ClassID)?.ClassName || null
                      : formData.ClassID
                  )
                }
                onChange={(event, newValue) => {
                  const className = newValue || '';
                  setFormData(prev => ({ ...prev, ClassID: className }));
                  // Also update selectedClass to keep in sync
                  if (className) {
                    const fundClass = (fundClasses || []).find(cls => 
                      cls && cls.ClassName === className && 
                      cls.BackstopID != null && String(cls.BackstopID) === formData.BackstopID
                    );
                    if (fundClass) {
                      setSelectedClass(String(fundClass.ClassID));
                    }
                  } else {
                    setSelectedClass('');
                  }
                }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Fund Class"
                      fullWidth
                      placeholder="Select class"
                      size="small"
                    />
                  )}
                disabled={!formData.BackstopID}
                freeSolo
                blurOnSelect
              />

              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  label="Date *"
                  type="date"
                  value={formData.PerfDate}
                  onChange={(e) => handleInputChange('PerfDate', e.target.value)}
                  fullWidth
                  required
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  label="Performance (%)"
                  type="number"
                  value={formData.PerfValue}
                  onChange={(e) => handleInputChange('PerfValue', e.target.value)}
                  fullWidth
                  required
                  size="small"
                  inputProps={{ step: 0.01 }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={1}>
                <FormControl fullWidth size="small">
                  <InputLabel>Quality</InputLabel>
                  <Select
                    value={formData.PerfQualityID}
                    onChange={(e) => handleInputChange('PerfQualityID', e.target.value)}
                    label="Quality"
                  >
                    <MenuItem value="">
                      <em>All</em>
                    </MenuItem>
                    {perfQualities.map(quality => (
                      <MenuItem key={quality.PerfQualityID} value={quality.PerfQualityID.toString()}>
                        {quality.PerfQualityName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={investedOnly}
                        onChange={(e) => handleInvestedOnlyChange(e.target.checked)}
                        size="small"
                      />
                    }
                    label="Invested funds only"
                  />
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<ExportIcon />}
                      onClick={handleExportCSV}
                      disabled={filteredPerformances.length === 0}
                    >
                      Export CSV
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<PdfIcon />}
                      onClick={handleExportPDF}
                      disabled={filteredPerformances.length === 0}
                    >
                      Export PDF
                    </Button>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                  onClick={handleSubmit}
                  disabled={loading || !formData.BackstopID || !formData.ClassID || !formData.PerfValue}
                  sx={{ flex: 1, minWidth: '120px' }}
                >
                  {loading ? 'Processing...' : (editingId ? 'Update' : 'Save')}
                </Button>
                {hasChangesFromOriginal && (
                  <Tooltip title="Save as new record instead of updating" placement="top">
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={handleSaveAsNew}
                      disabled={loading || !formData.BackstopID || !formData.ClassID || !formData.PerfValue}
                      sx={{ flex: 1, minWidth: '120px' }}
                      color="success"
                    >
                      Add as New
                    </Button>
                  </Tooltip>
                )}
                {hasFilterCriteria && !editingId && (
                  <Tooltip title="Search (Ctrl+S)" placement="top">
                    <Button
                      variant="outlined"
                      startIcon={<SearchIcon />}
                      onClick={() => handleSearch(true)}
                      disabled={loading}
                      sx={{ flex: 1 }}
                    >
                      <Box component="span">
                        <Box component="span" sx={{ textDecoration: 'underline' }}>S</Box>earch
                      </Box>
                    </Button>
                  </Tooltip>
                )}
                <Tooltip title="Clear (Ctrl+L)" placement="top">
                  <Button
                    variant="outlined"
                    startIcon={<ClearIcon />}
                    onClick={handleClear}
                    disabled={loading}
                  >
                    <Box component="span">
                      C<Box component="span" sx={{ textDecoration: 'underline' }}>l</Box>ear
                    </Box>
                  </Button>
                </Tooltip>
                  <Button
                    variant="outlined"
                    startIcon={<UploadIcon />}
                    onClick={() => setImportDialogOpen(true)}
                    disabled={loading || !selectedFund}
                    color="secondary"
                  >
                    Import Track
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Table Section */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Box>
                <Typography variant="h6">
                  Performance History ({filteredPerformances.length} of {performances.length})
                </Typography>
                {hasAppliedFilters && (
                  <Typography variant="body2" color="text.secondary">
                    Filtered by form criteria
                  </Typography>
                )}
              </Box>
              {/* Refresh functionality now handled by global refresh button in tabs */}
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <SortableTableHeader
                      field="BackstopID"
                      label="BackstopID"
                      sortDirection={getSortDirection('BackstopID')}
                      sortIndex={getSortIndex('BackstopID')}
                      onSort={handleSort}
                    />
                    <SortableTableHeader
                      field="FundName"
                      label="Fund Name"
                      sortDirection={getSortDirection('FundName')}
                      sortIndex={getSortIndex('FundName')}
                      onSort={handleSort}
                    />
                    <SortableTableHeader
                      field="ClassName"
                      label="Class"
                      sortDirection={getSortDirection('ClassName')}
                      sortIndex={getSortIndex('ClassName')}
                      onSort={handleSort}
                    />
                    <SortableTableHeader
                      field="PerfDate"
                      label="Date"
                      sortDirection={getSortDirection('PerfDate')}
                      sortIndex={getSortIndex('PerfDate')}
                      onSort={handleSort}
                    />
                    <SortableTableHeader
                      field="PerfValue"
                      label="Value"
                      sortDirection={getSortDirection('PerfValue')}
                      sortIndex={getSortIndex('PerfValue')}
                      onSort={handleSort}
                    />
                    <SortableTableHeader
                      field="PerfQualityName"
                      label="Quality"
                      sortDirection={getSortDirection('PerfQualityName')}
                      sortIndex={getSortIndex('PerfQualityName')}
                      onSort={handleSort}
                    />
                    <SortableTableHeader
                      field="PerfTypeName"
                      label="Type"
                      sortDirection={getSortDirection('PerfTypeName')}
                      sortIndex={getSortIndex('PerfTypeName')}
                      onSort={handleSort}
                    />
                    <SortableTableHeader
                      field="Timestamp"
                      label="Timestamp"
                      sortDirection={getSortDirection('Timestamp')}
                      sortIndex={getSortIndex('Timestamp')}
                      onSort={handleSort}
                    />
                    <SortableTableHeader
                      field="UserName"
                      label="User"
                      sortDirection={getSortDirection('UserName')}
                      sortIndex={getSortIndex('UserName')}
                      onSort={handleSort}
                    />
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedPerformances.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          No performances found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedPerformances.map((performance) => (
                      <TableRow 
                        key={performance.PerfID}
                        onDoubleClick={() => handleEdit(performance)}
                        sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#f5f5f5' } }}
                      >
                        <TableCell>{performance.BackstopID}</TableCell>
                        <TableCell>{performance.FundName}</TableCell>
                        <TableCell>{performance.ClassName}</TableCell>
                        <TableCell>{new Date(performance.PerfDate).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <Chip 
                            label={`${(performance.PerfValue * 100).toFixed(2)}%`}
                            size="small"
                            color={performance.PerfValue >= 0 ? 'success' : 'error'}
                          />
                        </TableCell>
                        <TableCell>{performance.PerfQualityName}</TableCell>
                        <TableCell>{performance.PerfTypeName}</TableCell>
                        <TableCell>{new Date(performance.Timestamp).toLocaleString()}</TableCell>
                        <TableCell>{performance.UserName}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => handleEdit(performance)}
                                disabled={loading}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setPerformanceToDelete(performance);
                                  setDeleteDialogOpen(true);
                                }}
                                disabled={loading}
                                color="error"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredPerformances.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(event, newPage) => setPage(newPage)}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(0);
              }}
            />
          </Paper>
        </Grid>
      </Grid>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete Performance</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the performance for "{performanceToDelete?.FundName}" 
            on {performanceToDelete?.PerfDate}?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" disabled={loading}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Dialog - Full Implementation */}
      <Dialog open={importDialogOpen} onClose={() => setImportDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Import Performance Track Record</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Import performance data from Excel file for fund: <strong>{(funds || []).find(f => f && f.BackstopID != null && f.BackstopID.toString() === selectedFund)?.FundName || 'Unknown'}</strong>
          </Typography>
          
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Import Settings</Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Quality</InputLabel>
                  <Select
                    value={formData.PerfQualityID}
                    onChange={(e) => handleInputChange('PerfQualityID', e.target.value)}
                    label="Quality"
                  >
                    {perfQualities.map(quality => (
                      <MenuItem key={quality.PerfQualityID} value={quality.PerfQualityID.toString()}>
                        {quality.PerfQualityName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={formData.PerfTypeID}
                    onChange={(e) => handleInputChange('PerfTypeID', e.target.value)}
                    label="Type"
                  >
                    {perfTypes.map(type => (
                      <MenuItem key={type.PerfTypeID} value={type.PerfTypeID.toString()}>
                        {type.PerfTypeName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Excel File Requirements</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Your Excel file should have exactly 2 columns:
            </Typography>
            <Box component="ul" sx={{ mt: 1, pl: 2, mb: 2 }}>
              <li><strong>Column 1:</strong> Date (any standard date format)</li>
              <li><strong>Column 2:</strong> Performance Value (as decimal, e.g., 0.0245 for 2.45%)</li>
            </Box>
            
            <input
              accept=".xlsx,.xls,.xlsm,.xlsb"
              style={{ display: 'none' }}
              id="excel-file-input"
              type="file"
              onChange={handleFileSelect}
            />
            <label htmlFor="excel-file-input">
              <Button variant="outlined" component="span" startIcon={<UploadIcon />}>
                Select Excel File
              </Button>
            </label>
            
            {selectedFile && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Selected: {selectedFile.name}
              </Typography>
            )}
          </Box>

          {importResults && (
            <Box sx={{ mb: 2 }}>
              <Alert severity={importResults.success ? "success" : "error"}>
                <Typography variant="body2">
                  {importResults.message}
                </Typography>
                {importResults.details && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2">
                      Imported: {importResults.details.imported_count} records
                    </Typography>
                    <Typography variant="body2">
                      Total rows processed: {importResults.details.total_rows_processed}
                    </Typography>
                    {importResults.details.errors && importResults.details.errors.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="body2" color="error">
                          Errors encountered:
                        </Typography>
                        <Box component="ul" sx={{ pl: 2 }}>
                          {importResults.details.errors.slice(0, 5).map((error, index) => (
                            <li key={index}>
                              <Typography variant="body2" color="error">
                                {error}
                              </Typography>
                            </li>
                          ))}
                          {importResults.details.errors.length > 5 && (
                            <li>
                              <Typography variant="body2" color="error">
                                ... and {importResults.details.errors.length - 5} more errors
                              </Typography>
                            </li>
                          )}
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialogOpen(false)}>Close</Button>
          <Button 
            onClick={handleImportTrack} 
            variant="contained" 
            disabled={!selectedFile || loading}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <UploadIcon />}
          >
            {loading ? 'Importing...' : 'Import Track Record'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Monthly ROR Upload Confirmation Dialog */}
      <Dialog open={rorDialogOpen} onClose={() => { setRorDialogOpen(false); setPendingRorUpload(null); }}>
        <DialogTitle>Upload Monthly ROR to Monitoring</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            This is an end of month performance value. Would you like to upload this value to Monitoring Data
            as this fund's <strong>Monthly ROR (%)</strong> for the selected month?
          </Typography>
          {pendingRorUpload && (
            <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="body2"><strong>Fund:</strong> {pendingRorUpload.fundName}</Typography>
              <Typography variant="body2"><strong>Date:</strong> {new Date(pendingRorUpload.perfDate + 'T00:00:00').toLocaleDateString()}</Typography>
              <Typography variant="body2"><strong>Value:</strong> {pendingRorUpload.perfValue.toFixed(2)}%</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setRorDialogOpen(false); setPendingRorUpload(null); }}>
            No, Skip
          </Button>
          <Button onClick={handleRorUpload} variant="contained" color="primary" disabled={rorUploading}>
            {rorUploading ? 'Uploading...' : 'Yes, Upload to Monitoring'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PerformanceManagement;
